import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Building2, LogOut, Users, FileText,
  Home, UserCog, CreditCard, Download, UserPlus, Phone, IndianRupee, Star, Gift,
} from "lucide-react";
import { api } from "@/lib/api";
import TenantsTab from "@/components/TenantsTab";
import GenerateReceiptTab from "@/components/GenerateReceiptTab";
import ReceiptsTab from "@/components/ReceiptsTab";
import HomeTab from "@/components/HomeTab";
import ProfileTab from "@/components/ProfileTab";
import PendingTab from "@/components/PendingTab";
import SubscriptionTab from "@/components/SubscriptionTab";
import ModeratorsTab from "@/components/ModeratorsTab";
import ContactTab from "@/components/ContactTab";
import FeedbackTab from "@/components/FeedbackTab";
import ReferTab from "@/components/ReferTab";

const NAV = [
  { id: "home", label: "Home", icon: Home },
  { id: "tenants", label: "Add Tenants", icon: Users },
  { id: "profile", label: "Customize my Profile", icon: UserCog },
  { id: "generate", label: "Generate Receipts", icon: FileText },
  { id: "pending", label: "Pending Payments", icon: IndianRupee },
  { id: "subscription", label: "Subscription", icon: CreditCard },
  { id: "downloads", label: "Downloads", icon: Download },
  { id: "moderator", label: "Add Moderator", icon: UserPlus },
  { id: "feedback", label: "Feedback", icon: Star },
  { id: "refer", label: "Refer a PG", icon: Gift, soon: true },
  { id: "contact", label: "Contact Us", icon: Phone },
];

export default function Dashboard() {
  const { owner, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [tab, setTab] = useState("home");

  const refresh = async () => {
    const [s, t, r] = await Promise.all([
      api.get("/stats"),
      api.get("/tenants"),
      api.get("/receipts"),
    ]);
    setStats(s.data); setTenants(t.data); setReceipts(r.data);
  };

  useEffect(() => { refresh(); }, []);

  const handleLogout = () => { logout(); navigate("/"); };

  const content = {
    home: <HomeTab owner={owner} stats={stats} receipts={receipts} onNavigate={setTab} />,
    tenants: <TenantsTab tenants={tenants} onChange={refresh} />,
    profile: <ProfileTab owner={owner} />,
    generate: <GenerateReceiptTab tenants={tenants} owner={owner} onGenerated={() => refresh()} />,
    pending: <PendingTab owner={owner} />,
    subscription: <SubscriptionTab stats={stats} onNavigate={setTab} />,
    downloads: <ReceiptsTab receipts={receipts} owner={owner} />,
    moderator: <ModeratorsTab />,
    feedback: <FeedbackTab />,
    refer: <ReferTab />,
    contact: <ContactTab />,
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] md:flex">
      {/* Sidebar (desktop) */}
      <aside className="hidden md:flex flex-col w-60 shrink-0 bg-white border-r border-slate-200 sticky top-0 h-screen">
        <div className="p-5 border-b border-slate-100 flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-indigo-600 to-blue-600 flex items-center justify-center shrink-0">
            <Building2 className="h-5 w-5 text-white" />
          </div>
          <div className="min-w-0">
            <p className="font-display font-bold text-sm leading-tight truncate" data-testid="sidebar-property-name">{owner?.property_name}</p>
            <p className="text-[11px] text-slate-500 leading-tight truncate">{owner?.stay_type} • {owner?.address_city}</p>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
          {NAV.map(({ id, label, icon: Icon, soon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              data-testid={`nav-${id}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                tab === id ? "bg-indigo-50 text-indigo-700" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
              }`}
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span className="text-left">{label}</span>
              {soon && <span className="ml-auto text-[9px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full" data-testid={`nav-${id}-soon-badge`}>Soon</span>}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-100">
          <Button variant="outline" size="sm" className="w-full" onClick={handleLogout} data-testid="dashboard-nav-logout-button">
            <LogOut className="h-4 w-4 mr-1.5" /> Logout
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-200">
        <div className="flex items-center justify-between px-4 h-14">
          <p className="font-display font-bold text-sm truncate">{owner?.property_name}</p>
          <Button variant="outline" size="sm" onClick={handleLogout} data-testid="dashboard-nav-logout-button-mobile">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
        <nav className="flex overflow-x-auto px-2 pb-2 gap-1">
          {NAV.map(({ id, label }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              data-testid={`nav-${id}-mobile`}
              className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium ${
                tab === id ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600"
              }`}
            >
              {label}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8 max-md:pt-32">
        {content[tab]}
        <footer className="mt-10 pt-4 border-t border-slate-200 text-center text-xs text-slate-500">
          © 2026 PGRentReceipts. All rights reserved.
        </footer>
      </main>
    </div>
  );
}

